import React, { useEffect, useMemo } from 'react';
import { Box, Typography, Button, Divider, Stack, Icons } from '@curves/ui-base';
import { useHistory, useLocation } from 'react-router-dom';
import { useMainLayoutContext } from '@Local/Components/Layout/Context/MainLaydoutContext';
import { FOOTER_TYPE, HEADER_TYPE, THEME_TYPE } from '@Constants/dataEnums';
import {
    MEMBER_REDEEM_ROUTER,
    MEMBER_REDEEM_CONFIRM_ROUTER,
    MEMBER_REDEEM_PRODUCT_GUIDE_ROUTER,
    MEMBER_REDEEM_PRODUCT_SELECT_ROUTER,
} from '@Router/routerPath';
import { RedeemStepper, UnredeemConfirmMessage } from '@Local/Redeem/Components';
import useRedeemCart from '@Local/Redeem/hooks/useRedeemCart';
import useMemberRedeemApi from '@Apis/useMemberRedeemApi';
import useMessageDialog from '@Util/hook/useMessageDialog';
import { displayDateText, getDate, isAfter, isBefore } from '@Util/moment';
import shouldShowRedeemGuideCapsule from './shouldShowRedeemGuideCapsule';
import { buildGuideHandoffState, resolveProductSelectDraft } from './redeemProductGuideDraft';

// 不可兌換時的活動狀態
const ACTIVITY_STATUS = {
    NOT_STARTED: 'notStarted',
    ENDED: 'ended',
};

// 將購物車 map 轉為對話框可顯示的品項清單；僅保留目前活動仍存在且數量大於 0 的品項
const toConfirmItems = (cartMap = {}, products = []) =>
    products
        .filter((product) => (cartMap[product.itemID] || 0) > 0)
        .map((product) => ({
            itemID: product.itemID,
            itemName: product.itemName,
            quantity: cartMap[product.itemID],
        }));

/**
 * 兌換選品頁。調整數量後往確認頁，或直接送出「本次不兌換」；點縮圖膠囊看贈品說明。
 * 入口：RedeemPage → handleGoToSelect（location.state: { sourceCart, products, redeemablePoints, currentPoints }）
 */
const RedeemProductSelectPage = () => {
    const { setHeaderType, setFooterType, setThemeType, setHeaderContent } = useMainLayoutContext();
    const history = useHistory();
    const location = useLocation();
    const {
        sourceCart = {},
        products = [],
        redeemablePoints = 0,
        currentPoints = 0,
        canRedeem = false,
        redeemStart = null,
        redeemEnd = null,
    } = location.state || {};
    const { postUnRedeemApi } = useMemberRedeemApi();
    const setMessageDialog = useMessageDialog();

    const {
        cart,
        usedPoints,
        remainingPoints,
        totalItems,
        isOverBudget,
        isPointsInsufficient,
        canAddItem,
        addItem,
        removeItem,
        resetCart,
    } = useRedeemCart(products, redeemablePoints);

    // 功能：守衛（未傳 products 時轉回列表）＋設定版面
    useEffect(() => {
        setHeaderType(HEADER_TYPE.DEFAULT);
        setFooterType(FOOTER_TYPE.HIDE);
        setThemeType(THEME_TYPE.DEFAULT);
        setHeaderContent(<RedeemStepper activeStep={0} onBack={() => history.goBack()} />);

        if (!location.state?.products) history.replace(MEMBER_REDEEM_ROUTER);

        return () => setHeaderContent(null);
        // eslint-disable-next-line
    }, [history, setHeaderType, setFooterType, setThemeType, setHeaderContent]);

    // 功能：初始化草稿（僅初始化一次）
    useEffect(() => {
        resetCart(resolveProductSelectDraft(location.state));
        // eslint-disable-next-line
    }, []);

    // 功能：從說明頁返回時還原捲動位置
    useEffect(() => {
        if (typeof location.state?.scrollTop === 'number') {
            window.scrollTo(0, location.state.scrollTop);
        }
        // eslint-disable-next-line
    }, []);

    // 功能：原本已確認的兌換項目（進入本頁時的 sourceCart，後端已扣點）
    const sourceItems = useMemo(() => toConfirmItems(sourceCart, products), [sourceCart, products]);

    // 功能：目前清單選擇的項目（本頁草稿）
    const draftItems = useMemo(() => toConfirmItems(cart, products), [cart, products]);

    // 功能：不可兌換時判斷屬「尚未開始」或「已結束」，以顯示對應警告
    // 比對到「日」為止（isBefore/isAfter 預設 YYYY-MM-DD），故起訖當日皆視為在期間內；
    // isBefore/isAfter 遇無效日期會回傳空字串，故以 === true 明確判定，避免把空字串當成成立。
    const activityStatus = useMemo(() => {
        if (canRedeem) return null;

        const today = getDate();

        if (redeemStart && isBefore(today, redeemStart) === true) return ACTIVITY_STATUS.NOT_STARTED;
        if (redeemEnd && isAfter(today, redeemEnd) === true) return ACTIVITY_STATUS.ENDED;

        // 在期間內卻仍不可兌換（例如無進行中活動）時不臆測原因
        return null;
    }, [canRedeem, redeemStart, redeemEnd]);

    // 功能：兌換期間文字（Start ~ End），僅一端有值時只顯示該端，兩端皆無則為空字串
    const activityPeriodText = useMemo(
        () => displayDateText(redeemStart, redeemEnd, { defaultText: '' }),
        [redeemStart, redeemEnd],
    );

    if (!location.state?.products) return null;

    // 功能：送出本次不兌換，成功後導回列表頁
    const submitUnredeem = async () => {
        const isSuccess = await postUnRedeemApi();
        if (isSuccess) history.replace(MEMBER_REDEEM_ROUTER);
        // 失敗：留在本頁（useAxios 既有錯誤提示負責回饋）
    };

    // 功能：本次不兌換。已有選擇項目時先二次確認，並列出將被取消／不會送出的內容
    const handleUnredeem = () => {
        // 原本與目前都沒有選擇項目時，不兌換不會影響任何內容，無須確認直接送出
        if (sourceItems.length === 0 && draftItems.length === 0) {
            submitUnredeem();
            return;
        }

        setMessageDialog({
            open: true,
            title: '確認本次不兌換',
            MsgComp: UnredeemConfirmMessage,
            msgProps: { sourceItems, draftItems },
            buttonText: '確認不兌換',
            onAsyncConfirm: async (e, closeDialog) => {
                closeDialog();
                await submitUnredeem();
            },
        });
    };

    // 功能：開啟說明頁。先 replace 寫回草稿與捲動位置，返回時才還原得回來
    const handleOpenGuide = (product) => {
        const scrollTop = window.scrollY || window.pageYOffset || 0;
        history.replace(MEMBER_REDEEM_PRODUCT_SELECT_ROUTER, buildGuideHandoffState(location.state, cart, scrollTop));
        history.push(MEMBER_REDEEM_PRODUCT_GUIDE_ROUTER, { product });
    };

    // 功能：確認選品，帶入草稿前往確認頁（兌換期間一併帶入，返回修改時才不會遺失）
    const handleNext = () => {
        history.replace(MEMBER_REDEEM_CONFIRM_ROUTER, {
            cart,
            products,
            redeemablePoints,
            currentPoints,
            canRedeem,
            redeemStart,
            redeemEnd,
        });
    };

    return (
        <Box className="redeem-product-select-page">
            <Box className="container">
                <Typography className="redeem-ps-title">選擇兌換商品</Typography>

                {/* 商品清單 */}
                <Box className="redeem-ps-product-list">
                    {products.map((product, index, arr) => {
                        // isEnabled === true 代表有庫存可兌，false 為無庫存(不可兌)
                        const isSoldOut = !product.isEnabled;
                        const qty = cart[product.itemID] || 0;
                        const isAddDisabled = !canAddItem(product.points);
                        // 無庫存且未選才顯示「無庫存」標籤；若上次已選(qty>0)仍給加減控制，讓會員能減到 0 改選別的
                        const showSoldOutLabel = isSoldOut && qty === 0;
                        const cardClass = [
                            'redeem-product-card',
                            qty > 0 ? 'is-selected' : '',
                            isSoldOut ? 'is-soldout' : '',
                        ]
                            .filter(Boolean)
                            .join(' ');
                        const hasGuide = shouldShowRedeemGuideCapsule(product);
                        const thumbImg = product.imageSrc ? (
                            <img className="redeem-product-img" src={product.imageSrc} alt={product.itemName} />
                        ) : (
                            <Box className="redeem-product-img redeem-product-img-placeholder" />
                        );

                        return (
                            <React.Fragment key={product.itemID}>
                                <Box className={cardClass}>
                                    {hasGuide ? (
                                        <button
                                            type="button"
                                            className="redeem-product-thumb"
                                            aria-label={`查看「${product.itemName}」的贈品說明`}
                                            onClick={() => handleOpenGuide(product)}
                                        >
                                            {thumbImg}
                                            <Box className="redeem-product-guide-badge">
                                                <Icons.InfoOutlined />
                                                <span>說明</span>
                                            </Box>
                                        </button>
                                    ) : (
                                        <Box className="redeem-product-thumb">{thumbImg}</Box>
                                    )}
                                    <Box className="redeem-product-info">
                                        <Typography className="redeem-product-name">{product.itemName}</Typography>
                                        <Typography className="redeem-product-pts">{product.points} 點 / 件</Typography>
                                    </Box>
                                    {showSoldOutLabel ? (
                                        <Typography component="span" className="redeem-product-soldout">
                                            無庫存
                                        </Typography>
                                    ) : (
                                        <Box className="redeem-qty-control">
                                            <button
                                                className="redeem-qty-btn"
                                                onClick={() => removeItem(product.itemID)}
                                                disabled={qty === 0 || !canRedeem}
                                            >
                                                −
                                            </button>
                                            <Typography component="span" className="redeem-qty-value">{qty}</Typography>
                                            <button
                                                className="redeem-qty-btn"
                                                onClick={() => addItem(product.itemID, product.points)}
                                                disabled={isAddDisabled || !canRedeem}
                                            >
                                                +
                                            </button>
                                        </Box>
                                    )}
                                </Box>
                                {index < arr.length - 1 && <Divider />}
                            </React.Fragment>
                        );
                    })}
                </Box>

                {/* 固定底部：已選摘要 + 操作按鈕 */}
                <Box className="redeem-ps-footer">
                    <Box className="redeem-ps-summary-row">
                        <Typography component="span" className="redeem-ps-remain-label">剩餘</Typography>
                        <Typography component="span" className={`redeem-ps-remain-val${isOverBudget ? ' is-over' : ''}`}>
                            {Math.max(0, remainingPoints)}
                        </Typography>
                        <Typography component="span" className="redeem-ps-remain-unit">點</Typography>
                        <Typography component="span" className="redeem-ps-divider">·</Typography>
                        <Typography component="span" className="redeem-ps-summary-text">
                            已選 {totalItems} 件，已使用 {usedPoints} 點
                        </Typography>
                    </Box>
                    {!canRedeem ? (
                        <Box className="redeem-ps-summary-hint">
                            <Typography className="redeem-ps-hint-text">
                                {activityStatus === ACTIVITY_STATUS.ENDED
                                    ? '兌換活動已結束，無法進行兌換'
                                    : activityStatus === ACTIVITY_STATUS.NOT_STARTED
                                      ? '兌換活動尚未開始，開始後才可進行兌換'
                                      : '目前無法進行兌換'}
                            </Typography>
                            {activityPeriodText && (
                                <Typography className="redeem-ps-hint-period">
                                    兌換時間 {activityPeriodText}
                                </Typography>
                            )}
                        </Box>
                    ) : (
                        isPointsInsufficient && (
                            <Typography className="redeem-ps-summary-hint">
                                剩餘點數不足，無法再兌換
                            </Typography>
                        )
                    )}
                    <Stack direction="row" spacing={1} className="redeem-ps-btn-row">
                        <Button variant="outlined" onClick={handleUnredeem} disabled={!canRedeem} className="redeem-ps-skip">
                            本次不兌換
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleNext}
                            disabled={!canRedeem || totalItems === 0 || isOverBudget}
                            className="redeem-ps-confirm"
                        >
                            下一步
                        </Button>
                    </Stack>
                </Box>
            </Box>
        </Box>
    );
};

export default RedeemProductSelectPage;
